import dynamic from "next/dynamic";

import { useCallback, useState } from "react";

import { twMerge } from "tailwind-merge";

import { faEnvelope } from "@fortawesome/free-regular-svg-icons/faEnvelope";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useAuthentication from "hooks/useAuthentication";

const NewPrivateMessageModal = dynamic(
  () => import("components/NewPrivateMessageModal"),
  { ssr: false }
);

type MessageButtonProps = {
  profile: any;
  className?: string;
  hoverClassName?: string;
  children?: React.ReactNode;
  defaultMessage?: string;
  showIcon?: boolean;
  alwaysShow?: boolean;
};

const MessageButton = ({
  profile,
  className,
  hoverClassName,
  children,
  defaultMessage,
  showIcon,
  alwaysShow,
}: MessageButtonProps) => {
  const { authenticated, account: loggedInAccount } = useAuthentication();
  const { toggleModal } = useSignInSignUp();

  const [showNewMessageModal, setShowNewMessageModal] = useState(false);

  const message = useCallback(() => {
    if (!authenticated) {
      toggleModal("sign-in");
      return;
    }

    if (!profile?.canMessage) return;

    setShowNewMessageModal(true);
  }, [authenticated, profile?.canMessage, toggleModal]);

  if (!profile?.canMessage) {
    if (profile?.canEmail && loggedInAccount?.canSendEmail) {
      return (
        <a
          href={`mailto:${profile?.maskedEmail}`}
          className={twMerge(
            !children &&
              "bg-white border border-solid border-smart-teal rounded-full",
            !children && "hover:bg-smart-teal hover:text-white",
            !children && "text-smart-teal font-medium",
            !children && "leading-[1] md:leading-normal",
            !children && "text-[13px] md:text-[15px] text-center",
            !children && "px-[1.25em] py-[0.75em] md:py-[0.75em] md:px-[1.1em]",
            className,
            hoverClassName
          )}
          onClick={message}
        >
          {showIcon && (
            <FontAwesomeIcon icon={faEnvelope} className="mr-[5px]" />
          )}
          {children || "Email"}
        </a>
      );
    }
  }

  if (
    ((loggedInAccount && !loggedInAccount.canUsePrivateMessages) ||
      (!loggedInAccount && !alwaysShow)) &&
    !profile?.canMessage
  ) {
    return <></>;
  }

  return (
    <>
      <button
        disabled={!profile?.canMessage}
        className={twMerge(
          !children &&
            "bg-white border border-solid border-smart-teal rounded-full",
          // !children &&
          //   profile?.canMessage &&
          //   "hover:bg-smart-teal hover:text-white",
          !children && "text-smart-teal font-medium",
          !children && "leading-[1] md:leading-normal",
          !children && "text-[13px] md:text-[15px]",
          !children && "px-[1.25em] py-[0.75em] md:py-[0.75em] md:px-[1.1em]",
          !profile?.canMessage && "opacity-50",
          className,
          profile?.canMessage && hoverClassName
        )}
        onClick={message}
      >
        {showIcon && <FontAwesomeIcon icon={faEnvelope} className="mr-[5px]" />}
        {children || "Message"}
      </button>

      {showNewMessageModal && (
        <NewPrivateMessageModal
          preselectRecipients={[profile]}
          defaultMessage={defaultMessage}
          onClose={() => setShowNewMessageModal(false)}
        />
      )}
    </>
  );
};

export default MessageButton;
